import { Grid, IconButton, InputAdornment, Typography } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import React, { ChangeEvent, useRef, useState } from 'react';
import BaseButton from 'src/common/components/base-button';
import { IShowPassword } from 'src/common/interfaces/dtos/IShowPassword';
import { useTranslation } from 'react-i18next';
import { CssTextField } from 'src/css-text-field/css-text-field';
import { newStyles as loginStyles } from 'src/touchpoint/components/login-emerald-styles';
import { styles } from './authentication-modal-styles';
import EmeraldDialog from '../emerald-dialog/emerald-dialog';
import { ReactComponent as Alert } from 'src/common/media/pay-attention.svg';

export interface IAuthenticationModalProps {
  onSubmit: (
    userName: string,
    password: string,
    setError: any,
    isButtonDisabled: any,
  ) => Promise<void>;
  onCancel: () => void;
  title?: string;
  dataTestId?: string;
  usernameLabel?: string;
  passwordLabel?: string;
  submitLabel?: string;
  cancelLabel?: string;
}

export const AuthenticationModal = (modalProps: IAuthenticationModalProps): JSX.Element => {
  const { t } = useTranslation();

  const dataTestId = modalProps.dataTestId || 'authentication-modal';
  const modalTitle = t(modalProps.title || 'AuthModalTitle');
  const submitLabel = t(modalProps.submitLabel || 'AuthModalSubmit');
  const cancelLabel = t(modalProps.cancelLabel || 'AuthModalCancel');
  const usernameLabel = t(modalProps.usernameLabel || 'AuthModalUserId');
  const passwordLabel = t(modalProps.passwordLabel || 'AuthModalPassword');

  const trainingMode = false;
  const loginClasses = loginStyles();
  const classes = styles();

  const [userName, setUserName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>();
  const [buttonDisabled, isButtonDisabled] = useState<boolean>(true);

  const [showPassword, setShowPassword] = useState<IShowPassword>({ displayPassword: false });

  const usernameInputRef = useRef<HTMLInputElement>(null);

  const handleClickShowPassword = (): void => {
    setShowPassword({
      ...showPassword,
      displayPassword: !showPassword.displayPassword,
    });
  };

  const onTextChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const id = e.target.id;
    const value = e.target.value;
    isButtonDisabled(false);
    switch (id) {
      case 'userId':
        setUserName(value);
        break;
      case 'password':
        setPassword(value);
        break;
    }
  };

  return (
    <EmeraldDialog
      open={true}
      fullWidth={true}
      fullScreen={true}
      data-testid={dataTestId}
      classes={{
        paper: classes.dialogPaper,
      }}
    >
      <Grid container direction='column' className={`${classes.mainContainer} fullHeight`}>
        <Grid
          container
          alignItems='center'
          className={classes.title}
          data-testid={`${dataTestId}-title`}
        >
          <Grid item className={classes.titleContainer}>
            <Typography className={classes.titleContent}>{modalTitle}</Typography>
            <Grid item xs={1}>
              <Typography className={loginClasses.typographytitlecontainer} />
            </Grid>
          </Grid>
        </Grid>

        <Grid container justifyContent='center' style={{ marginTop: '10.612vh' }}>
          <Grid container item sm={8}>
            <Grid container justifyContent='center'>
              <Grid container item className={classes.loginFormContainer}>
                <form
                  className={loginClasses.loginform}
                  noValidate
                  data-testid={`${dataTestId}-login-form`}
                >
                  <Grid
                    container
                    className={
                      trainingMode ? loginClasses.formErrorTraining : loginClasses.formError
                    }
                  >
                    <Grid item container className={classes.errorMessageContainer}>
                      {error && (
                        <>
                          <Alert className={classes.erroricon} />
                          <span
                            className={classes.errorMessage}
                            data-testid={`${dataTestId}-FormErrorLabel`}
                          >
                            {error}
                          </span>
                        </>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container className={classes.loginInputBox}>
                    <Grid item sm={12}>
                      <CssTextField
                        classes={{
                          root: trainingMode ? classes.loginFieldTraining : classes.loginField,
                        }}
                        error={!!error}
                        variant='standard'
                        margin='none'
                        fullWidth
                        inputProps={{
                          'data-testid': `${dataTestId}-userid-input`,
                          className: 'global-keyboard',
                        }}
                        id='userId'
                        label={usernameLabel}
                        name='userId'
                        onChange={onTextChange}
                        value={userName}
                        autoComplete='off'
                        inputRef={usernameInputRef}
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <CssTextField
                        classes={{
                          root: trainingMode ? classes.loginFieldTraining : classes.loginField,
                        }}
                        error={!!error}
                        id='password'
                        variant='standard'
                        margin='none'
                        fullWidth
                        inputProps={{
                          'data-testid': `${dataTestId}-password-input`,
                          className: 'global-keyboard',
                        }}
                        name='password'
                        label={passwordLabel}
                        type={showPassword.displayPassword ? 'text' : 'password'}
                        onChange={onTextChange}
                        value={password}
                        autoComplete='new-password'
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position='end'>
                              <IconButton
                                className={error ? classes.rediconbutton : classes.grayiconbutton}
                                edge='end'
                                aria-label='Toggle password visibility'
                                onClick={handleClickShowPassword}
                                tabIndex={-1}
                                data-testid={`${dataTestId}-toggle-password-btn`}
                              >
                                {showPassword.displayPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item sm={6}>
                      <BaseButton
                        className={classes.actionButton}
                        fullWidth
                        buttonType='white'
                        onClick={() => modalProps.onCancel()}
                        tabIndex={-1}
                        data-testid={`${dataTestId}-cancel-button`}
                      >
                        {cancelLabel}
                      </BaseButton>
                    </Grid>
                    <Grid item sm={6} className={loginClasses.submitContainer}>
                      <BaseButton
                        className={classes.actionButton}
                        fullWidth
                        buttonType='primary'
                        onClick={() =>
                          modalProps.onSubmit(userName, password, setError, isButtonDisabled)
                        }
                        tabIndex={-1}
                        disabled={buttonDisabled}
                        data-testid={`${dataTestId}-submit-button`}
                      >
                        {submitLabel}
                      </BaseButton>
                    </Grid>
                  </Grid>
                </form>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </EmeraldDialog>
  );
};

export default AuthenticationModal;
